<script lang="ts">
	import Link from '$lib/components/Link.svelte'
	import { page } from '$app/stores'
	import * as m from '$lib/paraglide/messages.js'
	import { getLocale } from '$lib/paraglide/runtime.js'

	const DECAP_BASE_URL = 'https://pauseai-cms.netlify.app/#/collections/posts/entries'
	const GITHUB_BASE_URL = 'https://github.com/PauseAI/pauseai-website/edit/main/src/'
	const GITHUB_ISSUES_URL = 'https://github.com/PauseAI/pauseai-website/issues/new'

	const markdownFiles = import.meta.glob('/src/posts/**/*.md')
	const svelteFiles = import.meta.glob('/src/routes/**/+page.svelte')

	$: pathname = $page.url.pathname
	const currentLocale: string = getLocale() // broaden to avoid "never"
	const isTranslatedPage = currentLocale != 'en'

	let editUrl: string | null = null
	let translationIssueUrl: string | null = null

	$: {
		editUrl = null
		translationIssueUrl = null

		// Get the English canonical path (without locale prefix)
		let englishPath = pathname.replace(new RegExp(`^/${currentLocale}(/|$)`), '$1')
		if (englishPath === '') englishPath = '/'

		// For translated pages, we need to create a translation issue URL
		if (isTranslatedPage) {
			const encodedTitle = encodeURIComponent(`Translation improvement for ${pathname}`)
			const encodedBody = encodeURIComponent(
				`## Translation Improvement Suggestion\n\n` +
					`**Page:** ${pathname}\n\n` +
					`### Issue Description\n` +
					`<!-- Describe what's wrong with the current translation -->\n\n` +
					`### Suggested Improvement\n` +
					`<!-- Instead of just suggesting a direct replacement, please describe how the translation should be improved. -->\n\n` +
					`### Translation Guidance\n` +
					`<!-- Provide guidance for the translation system, like "When translating this type of content into ${currentLocale}, please use more technical terminology" or "The tone should be more formal in this language" -->\n\n` +
					`### Is this a general issue?\n` +
					`<!-- Let us know if this issue might apply to other pages or content as well -->\n\n` +
					`---\n` +
					`Note: Our translations are generated by an AI system using translation prompts. Your feedback will help us improve these prompts rather than directly editing translated text.`
			)
			translationIssueUrl = `${GITHUB_ISSUES_URL}?title=${encodedTitle}&body=${encodedBody}`
		}

		let relativePath = englishPath == '/' ? '/src/routes/+page.svelte' : null
		if (!relativePath) {
			const markdownPath = `/src/posts${englishPath}.md`
			if (markdownFiles[markdownPath]) relativePath = markdownPath
		}
		if (!relativePath) {
			const sveltePath = `/src/routes${englishPath}/+page.svelte`
			if (svelteFiles[sveltePath]) relativePath = sveltePath
		}

		if (relativePath) {
			if (relativePath.endsWith('.md')) {
				// Always point to the original English content in the CMS
				editUrl = DECAP_BASE_URL + englishPath
			} else if (relativePath.endsWith('.svelte')) {
				const rootPath = relativePath.substring('/src/'.length)
				editUrl = GITHUB_BASE_URL + rootPath
			}
		}
	}
</script>

{#if isTranslatedPage}
	<div class="edit-options">
		{#if editUrl}
			<Link href={editUrl}>
				{m.footer_other_edit()}
			</Link>
		{/if}
		{#if translationIssueUrl}
			<Link href={translationIssueUrl}>
				{m.footer_other_l10n()}
			</Link>
		{/if}
	</div>
{:else if editUrl}
	<Link href={editUrl}>
		Edit page{#if editUrl.startsWith(GITHUB_BASE_URL)}&nbsp;on GitHub{/if}
	</Link>
{/if}

<style>
	.edit-options {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}
</style>
